import React, { useState, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';

import { login, setAuthState } from 'services/onboarding-service';
import { getTeamUrlFromUrl } from 'utils/url';
import { getUrlParams } from 'utils/index';
import { DEFAULT_LOGIN_ROUTE } from 'constants/index';

import { MeetingEmbedContext } from 'context/MeetingEmbedContext';

import Login from 'components/login';
import LoginAdditionalAuth from 'components/LoginAdditionalAuth';
import ForgottenPassword from 'components/forgotten-password';
import QuickSignup from 'components/QuickSignup';

const getRedirect = () => {
    const urlParams = getUrlParams();
    const redirect = urlParams.redirect ? decodeURIComponent(urlParams.redirect) : window.location?.pathname;

    if (['/signup/team', '/login'].includes(redirect)) {
        return '';
    }

    return redirect || DEFAULT_LOGIN_ROUTE;
}

const AppLogin = ({ onSignupClick }) => {
    const meetingEmbedCtx = useContext(MeetingEmbedContext);
    const params = useParams();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [stage, setStage] = useState(params.stage || null);
    const [additionalAuth, setAdditionalAuth] = useState(null);

    const selectTeam = useCallback((authProps, teamUrl = getTeamUrlFromUrl()) => {
        const { teamTokens } = authProps;

        if (teamTokens.some(({ team }) => team && team.url === teamUrl)) {
            return teamUrl;
        }

        if (teamTokens.length === 1) {
            return teamTokens[0].team.url;
        }

        return null;
    }, []);

    const loginToTeam = useCallback(authProps => {
        const teamUrl = selectTeam(authProps);

        const { masterToken, teamTokens } = authProps;

        setAuthState({
            masterToken,
            teamTokens,
            teamUrl,
            redirect: getRedirect()
        });
    }, [selectTeam]);

    const handleLoginSuccess = useCallback(async ({ email, password }) => {
        const authProps = await login({ email, password });

        if (authProps?.additionalAuth) {
            setStage('additionalAuth');
            setEmail(email);
            setPassword(password);
            setAdditionalAuth(authProps.additionalAuth);
            return;
        }

        loginToTeam(authProps);
    }, [loginToTeam]);

    const handleAdditionalAuthSuccess = useCallback(authProps => {
        loginToTeam(authProps);
    }, []);

    const handleForgottenPasswordClick = useCallback(email => {
        setEmail(email);
        setStage('forgottenPassword');
    }, []);

    const handleBackClick = useCallback(() => {
        setStage('login');
    }, []);

    const handleForgottenPasswordSuccess = useCallback(email => {
        setStage('login');
        setEmail(email);
    }, []);

    const handleSignupClick = useCallback(() => {
        if (meetingEmbedCtx?.inApp) {
            setStage('quickSignup');
            return;
        }

        onSignupClick && onSignupClick(email);
    }, [meetingEmbedCtx?.inApp, email]);

    if (stage === 'forgottenPassword') {
        return (
            <ForgottenPassword
                email={email}
                onBackClick={handleBackClick}
                onSuccess={handleForgottenPasswordSuccess}
            />
        );
    }

    if (stage === 'additionalAuth') {
        return (
            <LoginAdditionalAuth
                additionalAuth={additionalAuth}
                email={email}
                password={password}
                onSuccess={handleAdditionalAuthSuccess}
            />
        )
    }

    if (stage === 'quickSignup') {
        return (
            <QuickSignup onBack={handleBackClick} />
        );
    }

    return (
        <Login
            email={email}
            onForgottenPasswordClick={handleForgottenPasswordClick}
            onSignupClick={handleSignupClick}
            onSuccess={handleLoginSuccess}
        />
    )
}

export default AppLogin;
